<template>
    <App title="Codes">
        <div class="codes-page">

            <!-- Page Hero -->
            <div class="codes-hero">
                <div class="codes-hero-icon">
                    <i class="fas fa-ticket-alt"></i>
                </div>
                <div class="codes-hero-body">
                    <h4 class="codes-hero-title">Generated Codes</h4>
                    <p class="codes-hero-sub">Track and manage swap codes issued to riders</p>
                </div>
                <div class="codes-hero-stats">
                    <div class="codes-stat">
                        <span class="codes-stat-dot dot-completed"></span>
                        <span class="codes-stat-label">Completed</span>
                    </div>
                    <div class="codes-stat">
                        <span class="codes-stat-dot dot-pending"></span>
                        <span class="codes-stat-label">Pending</span>
                    </div>
                    <div class="codes-stat">
                        <span class="codes-stat-dot dot-incomplete"></span>
                        <span class="codes-stat-label">Incomplete</span>
                    </div>
                </div>
            </div>

            <!-- Table Card -->
            <div class="codes-table-card">
                <Block>
                    <template #header>
                        <h3 class="block-title">All Codes</h3>
                    </template>
                    <Datatable :options="options" :scopes="scope" />
                </Block>
            </div>

        </div>
    </App>
</template>

<script lang="ts">
import Block from '@/vue/components/Block.vue'
import Datatable from '@/vue/components/Datatable.vue'
import App from '@/vue/templates/App.vue'
import { CellRender } from '@/vue/utils/Datatable'
import { Component, toNative, Vue } from 'vue-facing-decorator'

@Component({
    components: {
        App,
        Block,
        Datatable,
    },
})
class Group extends Vue {

    scope = ['incomplete', 'completed', 'pending'];
    get buttons() {
        let buttons: any[] = []
        buttons.push('refresh')
        buttons.push('trashed')

        return buttons
    }

    get columns() {
        let columns: any[] = []

        columns.push({
            data: 'value',
            title: 'Generated Code',
        })

        columns.push({
            data: 'rider.fullname',
            title: 'Rider',
            render: (data) => !!data ? data : 'Unknown Rider',
        })

        columns.push({
            data: 'rider.riderable.title',
            title: 'Rider Station',
            render: (data) => !!data ? data : 'Unassigned',
        })

        columns.push({
            data: 'order.engine.serial',
            title: 'Engine',
        })

        columns.push({
            data: 'quantity',
            title: 'No. of Batteries Purchased',
        })
        columns.push({
            data: 'claim_status',
            title: 'Claim Status',
            render: (data: string) => {
                const map: Record<string, { cls: string; label: string }> = {
                    complete:   { cls: 'status-pill pill-completed',  label: 'Complete'   },
                    completed:  { cls: 'status-pill pill-completed',  label: 'Completed'  },
                    pending:    { cls: 'status-pill pill-pending',    label: 'Pending'    },
                    incomplete: { cls: 'status-pill pill-incomplete', label: 'Incomplete' },
                }
                const entry = map[data?.toLowerCase()] ?? { cls: 'status-pill pill-unknown', label: data ?? '—' }
                return `<span class="${entry.cls}">${entry.label}</span>`
            },
        })

        columns.push({
            data: 'remains',
            title: 'No. of Claims Remaining',
        })

        columns.push({
            data: 'created_at',
            title: 'Created',
            render: CellRender.date(),
            orderable: true,
            name: 'created_at',
        })

        //columns.push(Column.actions(this.$gate))

        return columns
    }

    get options() {
        return {
            ajax: {
                url: this.route('code.ajax.table'),
                method: 'POST',
            },
            buttons: this.buttons,
            columns: this.columns,
            order: [[7, 'desc']],
        }
    }
}
export default toNative(Group)
</script>

<style scoped lang="scss">
$primary: #3D5A00;
$primary-light: #f0f4e6;
$border: #e9ecef;
$text-dark: #111827;
$text-mid: #374151;
$text-light: #6b7280;
$radius: 12px;
$shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
$shadow-hover: 0 4px 14px rgba(0, 0, 0, 0.09);

/* ── Page layout ── */
.codes-page {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

/* ── Hero ── */
.codes-hero {
    display: flex;
    align-items: center;
    gap: 1.1rem;
    background: #fff;
    border: 1px solid $border;
    border-left: 4px solid $primary;
    padding: 1.25rem 1.5rem;
    flex-wrap: wrap;
}

.codes-hero-icon {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: $primary-light;
    color: $primary;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
}

.codes-hero-body {
    flex: 1;
    min-width: 0;
}

.codes-hero-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: $text-dark;
    margin: 0 0 0.15rem;
}

.codes-hero-sub {
    font-size: 0.82rem;
    color: $text-light;
    margin: 0;
}

/* ── Status legend in hero ── */
.codes-hero-stats {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-left: auto;
    flex-wrap: wrap;
}

.codes-stat {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.codes-stat-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;

    &.dot-completed  { background: #16a34a; }
    &.dot-pending    { background: #d97706; }
    &.dot-incomplete { background: #dc2626; }
}

.codes-stat-label {
    font-size: 0.78rem;
    font-weight: 500;
    color: $text-mid;
}

/* ── Table card ── */
.codes-table-card {
    background: #fff;
    border: 1px solid $border;
    border-radius: $radius;
    box-shadow: $shadow;
    overflow: hidden;
    transition: box-shadow 0.2s;

    &:hover { box-shadow: $shadow-hover; }
}

/* ── Status pills — :deep() so DataTables-injected HTML is reached ── */
:deep(.status-pill) {
    display: inline-flex;
    align-items: center;
    padding: 0.22rem 0.7rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    line-height: 1;
    white-space: nowrap;
}

:deep(.pill-completed) {
    background: #dcfce7;
    color: #15803d;
    border: 1px solid #bbf7d0;
}

:deep(.pill-pending) {
    background: #fef3c7;
    color: #b45309;
    border: 1px solid #fde68a;
}

:deep(.pill-incomplete) {
    background: #fee2e2;
    color: #b91c1c;
    border: 1px solid #fecaca;
}

:deep(.pill-unknown) {
    background: #f3f4f6;
    color: #6b7280;
    border: 1px solid #e5e7eb;
}
</style>
